import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  RerunDbpjKitWorkerJobDocument,
  type RerunDbpjKitWorkerJobMutation,
  type RerunDbpjKitWorkerJobMutationVariables,
} from "~/features/dbpjkitWorkerJobs/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Rerun a DBPJKit worker job as a new run on the same job via `cnutRerunWorkerJob`.
 * The server restores the parameters persisted on the job, so the caller only
 * chooses dry run vs live. The rerun dialog is the confirmation step. `onRerun`
 * resolves `true` on success (letting the dialog close itself) and `false` when the
 * mutation failed — the error toast has already been surfaced by then.
 */
export function useDbpjkitWorkerJobRerun() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [rerunJob, { loading: rerunning }] = useMutation<
    RerunDbpjKitWorkerJobMutation,
    RerunDbpjKitWorkerJobMutationVariables
  >(RerunDbpjKitWorkerJobDocument);

  const onRerun = useCallback(
    async (jobId: string, dryRun: boolean) => {
      if (!tenantId) throw new Error("Tenant ID is required");
      try {
        const { data } = await rerunJob({
          variables: {
            input: { tenantId, jobId, dryRun },
          },
          // Refresh the latest-run badge (detail) and the run-history table. Deliberately
          // NOT awaited: the detail pages read these via useSuspenseQuery, and gating the
          // mutate promise on their refetch kept the dialog from ever closing. The
          // refetches still run in the background; success here means "the rerun was
          // accepted by Cnut".
          refetchQueries: ["GetDBPJKitWorkerJobs", "GetDBPJKitWorkerJobRuns"],
        });
        // cnutRerunWorkerJob returns a union; surface ValidationError so the toast shows it.
        const result = data?.cnutRerunWorkerJob;
        if (result?.__typename === "ValidationError") {
          throw new Error(result.fields.map((field) => field.message).join(", ") || t("error.unknown"));
        }
        toast.success(t("feature.common.rerunStartedSuccessfully", { model: "dbpjkitWorkerJob" }));
        return true;
      } catch (error) {
        toast.error(translateError(t, error));
        return false;
      }
    },
    [rerunJob, t, tenantId],
  );

  return { onRerun, rerunning };
}
